import { profileAtom } from "@/atoms";
import { FeedbackModal, LoadingOverlay } from "@/components/feedback";
import { DeleteIcon, Pin } from "@/icons";
import deleteAddress from "@/lib/delete-address";
import { Address } from "@/types";
import { useAtom, useSetAtom } from "jotai";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { AddressSummary, Card, EmptyState, Heading, IconButton, PageContainer } from "@/components/ui";

export function AddressesClient({ addresses }: { addresses: Address[] }) {
  const [loading, setLoading] = useState(false)
  const [profile, setProfile] = useAtom(profileAtom)

  useEffect(() => {
    setProfile((state) => {
      return !state ? state : {
        ...state,
        addresses
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses])

  return (
    <>
      {loading && <LoadingOverlay title="Atualizando informações" />}
      <PageContainer top="header-gap" bottom="chrome">
        <Heading level={1} className="sr-only">
          Meus Endereços
        </Heading>
        {profile && profile.addresses.length ? (
          <div className="flex flex-col gap-4">
            {profile.addresses.map(addr => (
              <AddressCard
                key={addr.id}
                address={addr}
                setLoadingAction={setLoading}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            size="section"
            icon={Pin}
            title="Você ainda não possui nenhum endereço registrado"
            description="Adicione um endereço para facilitar seus pedidos"
          />
        )}
      </PageContainer>
    </>
  )
}

function AddressCard({ address, setLoadingAction }: {
  address: Address
  setLoadingAction: Dispatch<SetStateAction<boolean>>
}) {
  const setProfile = useSetAtom(profileAtom)
  const [confirmOpen, setConfirmOpen] = useState(false)

  async function removeAddress(addressId: string) {
    setLoadingAction(true)
    await deleteAddress(addressId)

    setProfile((state) => {
      if (state) {
        const addresses = state.addresses.filter(address => address.id !== addressId)
        return { ...state, addresses }
      }

      return state
    })

    setLoadingAction(false)
  }

  function handleConfirmRemove() {
    setConfirmOpen(false)
    removeAddress(address.id)
  }

  return (
    <Card className="flex items-center justify-between gap-4">
      <AddressSummary address={address} size="lg" showIcon className="flex-1" />
      <IconButton
        icon={DeleteIcon}
        label="Remover endereço"
        size="icon-sm"
        iconWeight="regular"
        tone="danger"
        data-testid="address-remove"
        onClick={() => setConfirmOpen(true)}
      />
      <FeedbackModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        variant="warning"
        confirmTone="danger"
        title="Remover endereço?"
        description={(
          <p>
            Quer mesmo remover
            <br />
            <strong>{address.street}, {address.number}, {address.district} - {address.city}</strong>?
          </p>
        )}
        confirmLabel="Sim, remover"
        cancelLabel="Voltar"
        onConfirm={handleConfirmRemove}
      />
    </Card>
  )
}
